/**
 * TenantBrandingCard — admin-only section on UserSettingsPage that lets
 * the workspace admin upload + replace + remove a company logo.
 *
 * The logo is normalised server-side (resized to 400px max, stripped of
 * EXIF, re-encoded as PNG) before being stored in R2. It's then embedded
 * at the top of every payroll-grade timesheet export. Non-admin users get
 * nothing — the card returns null.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type CSSProperties } from "react";

import { api, extractErrorMessage } from "@/lib/api";
import type { TenantBranding } from "@/lib/types";
import { useAuthStore } from "@/store/auth";

const ADMIN_ROLES = new Set(["admin"]);
const ALLOWED_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const ALLOWED_EXT = [".png", ".jpg", ".jpeg", ".webp"];
const MAX_BYTES = 2 * 1024 * 1024;

function isAllowedLogo(file: File): boolean {
  if (ALLOWED_TYPES.has(file.type)) return true;
  const lower = file.name.toLowerCase();
  return ALLOWED_EXT.some((ext) => lower.endsWith(ext));
}

export function TenantBrandingCard() {
  const memberships = useAuthStore((s) => s.memberships);
  const isAdmin = memberships.some((m) => ADMIN_ROLES.has(m.role));
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const sigInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [sigError, setSigError] = useState<string | null>(null);

  const { data: branding, isLoading } = useQuery({
    queryKey: ["tenant-branding"],
    queryFn: async () => (await api.get<TenantBranding>("/tenant/branding")).data,
    enabled: isAdmin,
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file, file.name);
      return (await api.post<TenantBranding>("/admin/tenant/logo", form)).data;
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["tenant-branding"] });
    },
    onError: (err) =>
      setError(extractErrorMessage(err, "Logo upload failed.")),
  });

  const remove = useMutation({
    mutationFn: async () => {
      await api.delete("/admin/tenant/logo");
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["tenant-branding"] });
    },
    onError: (err) =>
      setError(extractErrorMessage(err, "Could not remove the logo.")),
  });

  const sigUpload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file, file.name);
      return (await api.post<TenantBranding>("/admin/tenant/signature", form)).data;
    },
    onSuccess: () => {
      setSigError(null);
      queryClient.invalidateQueries({ queryKey: ["tenant-branding"] });
    },
    onError: (err) => setSigError(extractErrorMessage(err, "Signature upload failed.")),
  });

  const sigRemove = useMutation({
    mutationFn: async () => {
      await api.delete("/admin/tenant/signature");
    },
    onSuccess: () => {
      setSigError(null);
      queryClient.invalidateQueries({ queryKey: ["tenant-branding"] });
    },
    onError: (err) =>
      setSigError(extractErrorMessage(err, "Could not remove the signature.")),
  });

  const onFileSelected = (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (!isAllowedLogo(file)) {
      setError("Logo must be a PNG, JPG, or WebP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`Logo must be ≤ ${Math.round(MAX_BYTES / 1024 / 1024)} MB.`);
      return;
    }
    upload.mutate(file);
  };

  const onSigSelected = (file: File | undefined) => {
    if (!file) return;
    setSigError(null);
    if (!isAllowedLogo(file)) {
      setSigError("Signature must be a PNG, JPG, or WebP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setSigError(`Signature must be ≤ ${Math.round(MAX_BYTES / 1024 / 1024)} MB.`);
      return;
    }
    sigUpload.mutate(file);
  };

  if (!isAdmin) return null;

  return (
    <section style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Company branding</h2>
        <span style={adminPill}>Admin</span>
      </div>
      <p style={{ fontSize: 13, color: "var(--body-mid)", margin: "0 0 16px" }}>
        Upload your company logo. It appears at the top of every timesheet
        export (Excel + PDF) so payroll documents carry your branding.
        Recommended: square PNG, transparent background, 200–400 px.
      </p>

      {isLoading ? (
        <p style={{ color: "var(--mute)", fontSize: 13 }}>Loading…</p>
      ) : branding?.logo_url ? (
        <div style={preview}>
          <img
            src={branding.logo_url}
            alt={`${branding.name} logo`}
            style={{
              width: 96,
              height: 96,
              objectFit: "contain",
              background: "var(--surface-1)",
              borderRadius: 4,
              border: "1px solid var(--hairline)",
            }}
          />
          <div style={{ flex: 1, fontSize: 13, color: "var(--body)" }}>
            <div style={{ fontWeight: 500 }}>
              {branding.logo_filename ?? "logo.png"}
            </div>
            <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 2 }}>
              {branding.logo_size_bytes
                ? `${(branding.logo_size_bytes / 1024).toFixed(1)} KB`
                : ""}
              {branding.logo_uploaded_at
                ? ` · uploaded ${new Date(branding.logo_uploaded_at).toLocaleDateString()}`
                : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={upload.isPending}
              style={btnSecondary}
            >
              Replace
            </button>
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              style={btnDanger}
            >
              {remove.isPending ? "Removing…" : "Remove"}
            </button>
          </div>
        </div>
      ) : (
        <div style={emptyDrop}>
          <p style={{ fontSize: 13, color: "var(--mute)", margin: "0 0 12px" }}>
            No logo uploaded yet.
          </p>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            style={btnPrimary}
          >
            {upload.isPending ? "Uploading…" : "Upload logo"}
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: "none" }}
        onChange={(e) => onFileSelected(e.target.files?.[0])}
      />

      {error && (
        <p role="alert" style={errorText}>
          {error}
        </p>
      )}

      {/* ---- Authorized signature ---- */}
      <div style={{ height: 1, background: "var(--hairline)", margin: "20px 0 16px" }} />
      <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>
        Authorized signature
      </h3>
      <p style={{ fontSize: 13, color: "var(--body-mid)", margin: "0 0 16px" }}>
        Upload an authorized-signatory image. It's placed on the signature line
        of every downloaded timesheet. Recommended: a transparent PNG of the
        signature, roughly 300×90 px.
      </p>

      {isLoading ? (
        <p style={{ color: "var(--mute)", fontSize: 13 }}>Loading…</p>
      ) : branding?.signature_url ? (
        <div style={preview}>
          <img
            src={branding.signature_url}
            alt="Authorized signature"
            style={{
              width: 160,
              height: 56,
              objectFit: "contain",
              background: "var(--surface-1)",
              borderRadius: 4,
              border: "1px solid var(--hairline)",
            }}
          />
          <div style={{ flex: 1, fontSize: 13, color: "var(--body)" }}>
            <div style={{ fontWeight: 500 }}>
              {branding.signature_filename ?? "signature.png"}
            </div>
            <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 2 }}>
              {branding.signature_size_bytes
                ? `${(branding.signature_size_bytes / 1024).toFixed(1)} KB`
                : ""}
              {branding.signature_uploaded_at
                ? ` · uploaded ${new Date(branding.signature_uploaded_at).toLocaleDateString()}`
                : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => sigInputRef.current?.click()}
              disabled={sigUpload.isPending}
              style={btnSecondary}
            >
              Replace
            </button>
            <button
              type="button"
              onClick={() => sigRemove.mutate()}
              disabled={sigRemove.isPending}
              style={btnDanger}
            >
              {sigRemove.isPending ? "Removing…" : "Remove"}
            </button>
          </div>
        </div>
      ) : (
        <div style={emptyDrop}>
          <p style={{ fontSize: 13, color: "var(--mute)", margin: "0 0 12px" }}>
            No signature uploaded yet.
          </p>
          <button
            type="button"
            onClick={() => sigInputRef.current?.click()}
            disabled={sigUpload.isPending}
            style={btnPrimary}
          >
            {sigUpload.isPending ? "Uploading…" : "Upload signature"}
          </button>
        </div>
      )}

      <input
        ref={sigInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: "none" }}
        onChange={(e) => onSigSelected(e.target.files?.[0])}
      />

      {sigError && (
        <p role="alert" style={errorText}>
          {sigError}
        </p>
      )}
    </section>
  );
}

// ---------- styles ----------

const card: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 20,
  marginBottom: 16,
};

const adminPill: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  padding: "2px 8px",
  background: "var(--ink)",
  color: "var(--on-primary)",
  borderRadius: 3,
};

const preview: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 16,
};

const emptyDrop: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: 16,
  background: "var(--surface-1)",
  border: "1px dashed var(--hairline)",
  borderRadius: 4,
};

const btnPrimary: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const btnSecondary: CSSProperties = {
  background: "transparent",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "7px 13px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const btnDanger: CSSProperties = {
  background: "transparent",
  color: "#c11930",
  border: "1px solid rgba(193,25,48,0.35)",
  borderRadius: 4,
  padding: "7px 13px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const errorText: CSSProperties = {
  marginTop: 12,
  padding: "8px 12px",
  fontSize: 13,
  color: "#ee1d36",
  background: "rgba(238,29,54,0.06)",
  border: "1px solid rgba(238,29,54,0.3)",
  borderRadius: 4,
};
